// src/components/OTPModal.js
import React, { useState, useRef } from "react";
import "./OTPModal.css"; // Create this CSS file

const OTPModal = ({
  isOpen,
  onClose,
  email,
  phone,
  onVerify,
  loading,
  error,
  onResend,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [method, setMethod] = useState("email"); // Default to email
  const otpRefs = useRef(
    Array(6)
      .fill()
      .map(() => React.createRef()),
  );

  if (!isOpen) return null;

  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.current?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (pasted.length === 6 && /^\d{6}$/.test(pasted)) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.current?.focus();
    }
  };

  const switchToMethod = async (newMethod) => {
    if (newMethod === method) return; // already selected → no-op

    setMethod(newMethod);
    setOtp(["", "", "", "", "", ""]);
    await onResend(newMethod); // resend to the newly selected method
  };

const maskedValue =
    method === 'email'
      ? email.replace(/(.{3}).+(@.+)/, '$1****$2')
      : phone
        ? `+234${phone.slice(0, 2)}*******${phone.slice(-3)}`
        : 'your phone number';

return (
    <div className="otp-modal-overlay">
      <div className="otp-modal">
        <button className="modal-close-btn" onClick={onClose}>×</button>

        <h3>Verify Your Account</h3>

        {/* Method selector – two buttons / tabs */}
        <div className="method-selector">
          <button
            type="button"
            className={`method-btn ${method === 'email' ? 'active' : ''}`}
            onClick={() => switchToMethod('email')}
            disabled={loading}
          >
            Email
          </button>
          <button
            type="button"
            className={`method-btn ${method === 'phone' ? 'active' : ''}`}
            onClick={() => switchToMethod('phone')}
            disabled={loading}
          >
            Phone
          </button>
        </div>

        <p className="otp-instruction">
          Enter the 6-digit code sent to your {method}:
          <br />
          <strong>{maskedValue}</strong>
        </p>

        <div className="otp-input-container">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              maxLength={1}
              value={digit}
              ref={otpRefs.current[index]}
              className="otp-input"
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              onPaste={handleOtpPaste}
              autoFocus={index === 0}
              inputMode="numeric" // better mobile keyboard
            />
          ))}
        </div>

        {error && <p className="otp-error">{error}</p>}

        <div className="otp-modal-buttons">
          <button className="btn-back" onClick={onClose} disabled={loading}>
            Back
          </button>
          <button
            className="btn-submit-otp"
            onClick={() => onVerify(otp.join(''), method)}
            disabled={loading || otp.some((v) => !v)}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        <p className="resend-text">
          Didn't receive the code?{' '}
          <button
            className="resend-link"
            onClick={() => onResend(method)}
            disabled={loading}
          >
            Resend
          </button>
        </p>
      </div>
    </div>
  );
};

export default OTPModal;